/**
 * Visits Left Action - WHAT Section
 *
 * Edit mode UI for sending visits left reminder.
 * Notifies clients when their package has a certain number of visits remaining.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-visits-left.hbs
 *
 * Fields:
 * - count: Number of visits left to trigger reminder
 * - reminderTypeId: Package IDs to filter by (optional array)
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { fetchPackagesByParams } from 'src/models/package/package';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * VisitsLeft WHAT Section - Edit mode
 */
export const VisitsLeftWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch packages
  const { data: packages = [] } = useQuery({
    queryKey: ['packages-all'],
    queryFn: () => fetchPackagesByParams({ fetch_all: true }),
    staleTime: 60000,
  });

  const selectedPackageIds: string[] = Array.isArray(automation.reminderTypeId)
    ? (automation.reminderTypeId as string[])
    : [];

  const selectedPackages = packages.filter((p) =>
    selectedPackageIds.includes(p.id.toString())
  );

  return (
    <Stack spacing={2}>
      {/* Package Filter */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.visitsLeft.forPackages')}</Box>
        <Box sx={{ minWidth: 250 }}>
          <Autocomplete
            multiple
            options={packages}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.name
            }
            value={selectedPackages}
            onChange={(_, newValue) => {
              setFieldValue(
                `automations.${index}.reminderTypeId`,
                newValue.map((p) => p.id.toString())
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('automations.visitsLeft.anyPackage')}
                size="small"
              />
            )}
            size="small"
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * VisitsLeft What Summary Component
 */
export const VisitsLeftWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();

  // Fetch packages for display
  const { data: packages = [] } = useQuery({
    queryKey: ['packages-all'],
    queryFn: () => fetchPackagesByParams({ fetch_all: true }),
    staleTime: 60000,
  });

  const selectedPackageIds: string[] = Array.isArray(automation.reminderTypeId)
    ? (automation.reminderTypeId as string[])
    : [];

  const packageNames = packages
    .filter((p) => selectedPackageIds.includes(p.id.toString()))
    .map((p) => p.name)
    .join(', ');

  if (packageNames) {
    return (
      <>
        {t('automations.visitsLeft.summaryWithPackages', {
          packages: packageNames,
        })}
      </>
    );
  }

  return <>{t('automations.visitsLeft.summary')}</>;
};

/**
 * VisitsLeft WHEN Section - Edit mode
 * "when a client has X visit(s) left for a user package"
 */
export const VisitsLeftWhenSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <Typography variant="body2">
        {t('automations.visitsLeft.whenClientHas')}
      </Typography>
      <TextField
        type="number"
        value={automation.count ?? 1}
        onChange={(e) =>
          setFieldValue(
            `automations.${index}.count`,
            parseInt(e.target.value, 10) || 0
          )
        }
        InputProps={{ inputProps: { min: 0 } }}
        size="small"
        sx={{ width: 80 }}
      />
      <Typography variant="body2">
        {t('automations.visitsLeft.visitsLeftForPackage')}
      </Typography>
    </Box>
  );
};

/**
 * VisitsLeft WHEN Summary
 */
export const VisitsLeftWhenSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const count = automation.count ?? 0;

  return (
    <>
      {t('automations.visitsLeft.whenSummary', { count })}
    </>
  );
};
